import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  ShieldAlert,
  Zap,
  Radar,
  Package,
  Crosshair,
  GitCommit,
  AlertTriangle,
  ShieldCheck,
  Gauge,
} from "lucide-react";
import SupportBanner from "../components/SupportBanner";
import FeedbackButton from "../components/FeedbackButton";

const FEATURES = [
  {
    icon: Search,
    title: "CVE Analysis",
    sub: "Paste any CVE-ID → get a full briefing in seconds",
    body: "Hit the search bar with something like CVE-2021-44228 and we go fetch NVD, MITRE, GitHub Security Advisories and the actual GitHub patch commit in parallel. AI then turns all of it into a plain-English root-cause walkthrough, attack flow steps, a sanitized pseudo-exploit, defensive recommendations, and an exploit-difficulty score.",
    use: "Triaging a Patch-Tuesday bulletin. Reading a CVE you saw on Twitter. Briefing a non-technical stakeholder.",
  },
  {
    icon: ShieldAlert,
    title: "Multi-Source Intelligence",
    sub: "NVD · MITRE · GitHub · OSV - all aggregated, deduped, linked",
    body: "Most analysts open four tabs to chase a CVE. We pull all four at once: NVD JSON 2.0 for CVSS + weaknesses, MITRE CNA container for the authoritative description, GitHub Security Advisories for ecosystem-aware ranges, and any commit/PR URLs referenced by NVD for the real patch diff.",
    use: "Verifying a CVSS dispute. Finding the actual fix commit. Knowing if a private advisory has been published.",
  },
  {
    icon: AlertTriangle,
    title: "Sanitized Pseudo-Exploit",
    sub: "Educational walkthrough - intentionally non-functional",
    body: "The AI produces a code sketch that shows the SHAPE of the attack with placeholders like <PAYLOAD_REDACTED> and <ATTACKER_HOST>. You learn what the attack looks like in logs and code-review without us shipping working weaponry. For sensitive CVEs the model can refuse this section entirely.",
    use: "Code review. Detection engineering inspiration. Teaching juniors what an attack looks like at the keyboard.",
  },
  {
    icon: Gauge,
    title: "Real-World Exploitability",
    sub: "How hard is it actually to pull off?",
    body: "Four separate, deterministic 0-10 measurements: Technical exploitability (CVSS attack vector, capped at Moderate unless a PoC or in-the-wild exploit exists), Threat maturity (EPSS, public PoC, CISA KEV, ransomware), Environmental exposure (estimated from CVSS for a default deployment) and Patch urgency. A CVSS 9.8 alone no longer reads as 'Trivial'. The old single score is still shown as a legacy reference. Verdict: Trivial / Easy / Moderate / Hard / Theoretical, plus attacker-skill ceiling and time-to-exploit.",
    use: "Defending a 'this CVE is critical' alert that's actually theoretical. Or the inverse - proving a 'medium' is trivially exploitable.",
  },
  {
    icon: Zap,
    title: "Patch Priority Score",
    sub: "Should I patch this RIGHT NOW?",
    body: "Composite operational score from CVSS (severity) + EPSS percentile (real-world exploit likelihood) + CISA KEV catalog (known active exploitation, with ransomware-use flag). Outputs EMERGENCY / PATCH NOW / THIS SPRINT / BACKLOG / INFORMATIONAL with an SLA window and a list of drivers. A remote, no-login, no-click code-execution bug is forced to EMERGENCY (24h when exploitation evidence exists, 72h otherwise) without changing its technical exploitability score.",
    use: "Daily vuln-management triage. Justifying a 2am pager call (or NOT calling).",
  },
  {
    icon: GitCommit,
    title: "GitHub Patch Diff Viewer",
    sub: "See exactly what changed to fix the bug",
    body: "When NVD references a commit or PR, we fetch the actual file-level diff from GitHub and render it inline with green/red gutter, hunk headers, and a link back to the source. The AI also produces a 2-3 sentence patch summary.",
    use: "Understanding the fix without leaving the page. Variant-hunting in your own codebase.",
  },
  {
    icon: Radar,
    title: "Detection Rules · 5 Formats",
    sub: "Sigma · YARA · KQL · Splunk · Suricata - SOC-ready",
    body: "One click and the AI generates production-grade detection content for every major SIEM/EDR: Sigma (with proper logsource taxonomy), YARA (rule blocks with strings + condition), KQL (against Sentinel/Defender tables), Splunk SPL (with eval/stats), Suricata (signed sid 1000000+). Plus IoC pivots, telemetry requirements, MITRE ATT&CK technique chips, and tuning notes.",
    use: "Detection engineering for a fresh CVE without writing Sigma at 11pm. Bootstrapping a content-as-code repo.",
  },
  {
    icon: Package,
    title: "SBOM Scanner + VEX Export",
    sub: "Find vulnerable deps. Triage. Export industry-standard VEX.",
    body: "Paste or upload a CycloneDX/SPDX SBOM, npm package.json, or pip requirements.txt. We match every component against OSV.dev, surface CVE-linked findings, let you mark each one with a VEX state (exploitable / not_affected / in_triage / false_positive / resolved) and a justification (code_not_reachable, requires_configuration, etc). One click downloads a CycloneDX 1.5 VEX JSON.",
    use: "Pre-merge dep audit. Customer-facing 'are we affected?' question. EO 14028 / CRA compliance evidence.",
  },
  {
    icon: Crosshair,
    title: "Async Job Pattern",
    sub: "Long-running work never times out anymore",
    body: "Detection regeneration runs as a background job: POST returns 202 + job_id, you poll /api/jobs/{id}, you can DELETE to cancel mid-flight. Race-safe, dedupe-by-target, 7-day TTL purge. Same primitive will be applied to /analyze and /sbom/scan next.",
    use: "Hitting Regenerate. Calling our API from a SOAR playbook. Building bots on top of us.",
  },
  {
    icon: ShieldCheck,
    title: "Rate Limits That Make Sense",
    sub: "Generous for humans · hostile to scrapers",
    body: "Per-IP: /analyze 8/min · 40/hr, /detections 5/min · 25/hr, /sbom/scan 5/min · 20/hr. Every response carries X-RateLimit-* headers and a Retry-After on 429. Job polling is intentionally unrestricted.",
    use: "Stops crawlers from burning the LLM budget. You probably will not notice these limits while using the UI.",
  },
];

const FAQ = [
  {
    q: "Will you ever produce a working exploit?",
    a: "No - the underlying model refuses, and we'd be on the wrong side of several jurisdictions' laws if we tried. Our pseudo-exploits are intentionally non-functional and contain placeholder values like <PAYLOAD_REDACTED>.",
  },
  {
    q: "Is my data shared?",
    a: "Analyses are cached globally in our database (other users may see that CVE-XXXX was analyzed) but we don't store IP addresses, user-agents, or anything that could identify you.",
  },
  {
    q: "How accurate is the priority score?",
    a: "The CVSS + EPSS + KEV inputs are authoritative - they come straight from FIRST.org and CISA. The composite formula is a transparent blend (CVSS*0.5 + EPSS*5, floored to 8.5 when in KEV, 9.5 when ransomware-used). Detection rules and pseudo-exploits are AI-generated - verify before deploying to production.",
  },
  {
    q: "Can I call this from my SOAR / Jira / Slack bot?",
    a: "Yes - every UI feature is backed by a documented JSON endpoint at /api/* and respects rate limits per source IP. The async-job pattern means you can fire-and-poll. A dedicated SDK + API tokens are on the roadmap.",
  },
  {
    q: "How is this free?",
    a: "It runs on weekends and goodwill, plus the Support button up top. If this tool has saved you time, $10 keeps the LLM credit topped up for the next defender.",
  },
];

export default function HelpPage() {
  return (
    <div className="App">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-3 flex items-center gap-6">
          <Link
            to="/"
            data-testid="back-to-dashboard"
            className="flex items-center gap-2 text-slate-700 hover:text-violet-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-display font-bold tracking-tight">
              CVE Analytics
            </span>
          </Link>
          <div className="overline hidden sm:block">Help · Feature reference</div>
          <div className="flex-1" />
          <FeedbackButton variant="header" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="mb-10">
          <div className="overline mb-3">Help · Feature reference</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-tight">
            What this tool actually does,
            <br />
            <span className="text-violet-700">and where each piece earns its keep.</span>
          </h1>
          <p className="text-slate-600 text-base md:text-lg mt-5 max-w-3xl leading-relaxed">
            Ten feature blocks below. Each one has a sentence on what it
            does, a paragraph on how it works, and a concrete use case so you
            know when to reach for it.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <section
                key={f.title}
                data-testid={`feature-${i}`}
                className="panel p-6 panel-hover"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="h-9 w-9 rounded-md bg-violet-50 border border-violet-200 grid place-items-center shrink-0">
                    <Icon className="h-4 w-4 text-violet-700" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-slate-900">{f.title}</h3>
                    <div className="text-xs font-mono text-slate-600 mt-0.5">
                      {f.sub}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">
                  {f.body}
                </p>
                <div className="pt-3 border-t border-slate-100">
                  <div className="overline mb-1">When to use it</div>
                  <p className="text-xs text-slate-600 leading-relaxed">{f.use}</p>
                </div>
              </section>
            );
          })}
        </div>

        <section className="mt-12">
          <div className="overline mb-4">Frequently asked</div>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <div
                key={i}
                data-testid={`faq-${i}`}
                className="panel p-5 panel-hover"
              >
                <h4 className="font-display text-base text-slate-900 mb-2">
                  {item.q}
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <SupportBanner />
      </main>
    </div>
  );
}
